import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { waitForSession } from "@/lib/auth-ready";
import { GrantsSiteHeader } from "@/components/grants-site-header";
import { posthog } from "@/lib/posthog";
import { SiteFooter } from "@/components/site-footer";
import { usePricing } from "@/lib/pricing";
import {
  countdownParts,
  formatShortDate,
  isVerifiedToday,
  programTypeLabel,
  programTypeTone,
  toneClasses,
} from "@/lib/grants-utils";

export const Route = createFileRoute("/grants/$id")({
  head: () => ({
    meta: [
      { title: "Grant details - DeckScore" },
      {
        name: "description",
        content: "Grant program details, eligibility, deadline and application link.",
      },
    ],
  }),
  component: GrantDetailPage,
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-bg p-10 text-center text-ink">{error.message}</div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen bg-bg p-10 text-center text-ink">Grant not found.</div>
  ),
});

type Grant = {
  id: string;
  name: string;
  scheme: string | null;
  country: string | null;
  regional_focus: string | null;
  host_organization: string | null;
  program_type: string | null;
  amount: string | null;
  closing_date: string | null;
  opening_date: string | null;
  is_rolling: boolean | null;
  status: string | null;
  updated_at: string | null;
  description: string | null;
  investment_thesis: string | null;
  sector_focus: string | null;
  eligibility: string[] | null;
  website_url: string | null;
  application_url?: string | null;
  emails?: string[] | null;
  linkedin_url?: string | null;
};

function GrantDetailPage() {
  const { id } = Route.useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [grant, setGrant] = useState<Grant | null>(null);
  const [loading, setLoading] = useState(true);
  const [nowTick, setNowTick] = useState(0);
  const pricing = usePricing();

  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const session = await waitForSession(4000);
      const authed = !!session;
      if (cancelled) return;
      setIsLoggedIn(authed);
      try {
        if (authed) {
          const { data } = await supabase
            .from("grants")
            .select(
              "id,name,scheme,country,regional_focus,host_organization,program_type,amount,closing_date,opening_date,is_rolling,status,updated_at,description,investment_thesis,sector_focus,eligibility,website_url,application_url,emails,linkedin_url",
            )
            .eq("id", id)
            .maybeSingle();
          if (!cancelled) setGrant((data ?? null) as Grant | null);
        } else {
          const { data } = await supabase
            .from("grants_public")
            .select(
              "id,name,scheme,country,regional_focus,host_organization,program_type,amount,closing_date,opening_date,is_rolling,status,updated_at,description,investment_thesis,sector_focus,eligibility",
            )
            .eq("id", id)
            .maybeSingle();
          if (!cancelled) setGrant((data ?? null) as unknown as Grant | null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Extension upsell now rendered inline as a card below the application link.


  if (loading) {
    return (
      <div className="min-h-screen bg-bg text-ink">
        <GrantsSiteHeader />
        <div className="mx-auto max-w-[1120px] px-5 py-16 text-muted-ink">Loading…</div>
      </div>
    );
  }
  if (!grant) {
    throw notFound();
  }

  const tone = programTypeTone(grant.program_type);
  const verifiedToday = isVerifiedToday(grant.updated_at);
  const parts = countdownParts(grant.closing_date);
  // consume nowTick so linter is happy and countdown re-renders every tick
  void nowTick;

  return (
    <div className="min-h-screen bg-bg text-ink">
      <GrantsSiteHeader />
      <main className="mx-auto max-w-[1120px] px-5 py-10">
        <nav className="text-[13px] text-muted-ink">
          <Link to="/grants" className="hover:text-ink">
            Grants
          </Link>
          <span className="mx-2 text-[color:var(--color-accent)]">/</span>
          {grant.country && (
            <>
              <span>{grant.country}</span>
              <span className="mx-2 text-[color:var(--color-accent)]">/</span>
            </>
          )}
          <span className="text-ink">{grant.name}</span>
        </nav>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${toneClasses(tone)}`}
          >
            {programTypeLabel(grant.program_type)}
          </span>
          {grant.country && (
            <span className="inline-flex items-center rounded-full border border-line bg-surface px-2.5 py-0.5 text-[11px] font-semibold text-ink">
              {grant.country}
            </span>
          )}
          {verifiedToday ? (
            <span className="inline-flex items-center rounded-full bg-[color:var(--color-accent-mint)] px-2.5 py-0.5 text-[11px] font-bold uppercase text-ink">
              ✓ Verified today
            </span>
          ) : grant.updated_at ? (
            <span className="inline-flex items-center rounded-full border border-line bg-surface px-2.5 py-0.5 text-[11px] font-semibold text-ink-tertiary">
              Verified {formatShortDate(grant.updated_at)}
            </span>
          ) : null}
        </div>

        <h1
          className="mt-4 text-[38px] font-bold leading-tight tracking-tight text-ink"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {grant.name}
        </h1>
        {(grant.scheme || grant.sector_focus) && (
          <div className="mt-1 text-[15px] text-muted-ink">
            {[grant.scheme, grant.sector_focus].filter(Boolean).join(" · ")}
          </div>
        )}

        {(() => {
          const applyHref = grant.application_url || grant.website_url;
          if (!applyHref) return null;
          if (isLoggedIn) {
            return (
              <a
                href={applyHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center justify-center rounded-full bg-accent px-6 py-3 text-[15px] font-bold text-accent-ink hover:bg-accent-press"
              >
                Apply now →
              </a>
            );
          }
          return (
            <Link
              to="/login"
              search={{ next: `/grants/${id}` }}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-3 text-[15px] font-bold text-accent-ink hover:bg-accent-press"
            >
              <Lock className="h-4 w-4" aria-hidden />
              Apply now
            </Link>
          );
        })()}



        <div className="mt-8 grid gap-8 md:grid-cols-[1fr_320px]">
          {/* LEFT */}
          <div className="flex flex-col gap-6">
            <section className="rounded-2xl border border-line bg-surface p-6">
              <h2
                className="text-[18px] font-bold text-ink"
                style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
              >
                About this program
              </h2>
              <div className="mt-3 flex flex-col gap-3 text-[14.5px] leading-relaxed text-ink">
                {grant.description ? <p>{grant.description}</p> : null}
                {grant.investment_thesis ? (
                  <p className="text-muted-ink">{grant.investment_thesis}</p>
                ) : null}
                {!grant.description && !grant.investment_thesis ? (
                  <p className="text-muted-ink">Details coming soon.</p>
                ) : null}
              </div>
            </section>

            <section className="rounded-2xl border border-line bg-surface p-6">
              <h2
                className="text-[18px] font-bold text-ink"
                style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
              >
                Eligibility checklist
              </h2>
              {grant.eligibility && grant.eligibility.length > 0 ? (
                <ul className="mt-4 flex flex-col gap-2.5 text-[14.5px] text-ink">
                  {grant.eligibility.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-bold text-ink"
                        style={{ background: "var(--color-accent-mint)" }}
                      >
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                (() => {
                  const link = grant.application_url || grant.website_url;
                  return (
                    <div className="mt-3 flex flex-col gap-3">
                      <p className="text-[14px] text-muted-ink">
                        We haven't summarized eligibility for this grant yet — check the official page:
                      </p>
                      {link ? (
                        isLoggedIn ? (
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex w-fit items-center rounded-full border border-line bg-surface px-4 py-2 text-[13px] font-bold text-ink hover:border-accent"
                          >
                            Open official page ↗
                          </a>
                        ) : (
                          <Link
                            to="/login"
                            search={{ next: `/grants/${id}` }}
                            className="inline-flex w-fit items-center rounded-full border border-line bg-surface px-4 py-2 text-[13px] font-bold text-ink hover:border-accent"
                          >
                            🔒 Free account to view official page ↗
                          </Link>
                        )
                      ) : null}
                    </div>
                  );
                })()
              )}
            </section>
          </div>

          {/* RIGHT SIDEBAR */}
          <aside className="flex flex-col gap-4 md:sticky md:top-24 md:self-start">
            <div className="rounded-2xl bg-ink p-6 text-[color:var(--color-bg)]">
              {grant.is_rolling ? (
                <>
                  <div
                    className="text-[10.5px] font-bold uppercase tracking-wider"
                    style={{ color: "var(--color-accent-lavender)" }}
                  >
                    Intake
                  </div>
                  <div
                    className="mt-2 text-[22px] font-bold"
                    style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                  >
                    Rolling intake
                  </div>
                </>
              ) : parts ? (
                <>
                  <div
                    className="text-[10.5px] font-bold uppercase tracking-wider"
                    style={{ color: "var(--color-accent-lavender)" }}
                  >
                    Closes in
                  </div>
                  <div className="mt-3 flex gap-2">
                    <TimeBox n={parts.d} l="days" />
                    <TimeBox n={parts.h} l="hrs" />
                    <TimeBox n={parts.m} l="min" />
                  </div>
                </>
              ) : (
                <div className="text-[13px]">Deadline TBD</div>
              )}
              <div className="my-4 h-px" style={{ background: "rgba(255,255,255,0.12)" }} />
              <div
                className="text-[10.5px] font-bold uppercase tracking-wider"
                style={{ color: "var(--color-accent-lavender)" }}
              >
                Grant amount
              </div>
              <div
                className="mt-1 text-[22px] font-bold"
                style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
              >
                {grant.amount ?? "-"}
              </div>
            </div>

            <div className="rounded-2xl border border-line bg-surface p-5">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-ink-tertiary">
                Official website
              </div>
              {isLoggedIn && grant.website_url ? (
                <a
                  href={grant.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1.5 block truncate text-[14px] font-semibold text-[color:var(--color-accent-press)] hover:underline"
                >
                  {(() => {
                    try {
                      return new URL(grant.website_url).hostname.replace(/^www\./, "");
                    } catch {
                      return grant.website_url;
                    }
                  })()}
                </a>
              ) : !isLoggedIn ? (
                <div
                  className="mt-1.5 truncate text-[14px] font-semibold text-ink"
                  style={{ filter: "blur(5px)" }}
                >
                  programwebsite.org
                </div>
              ) : null}

              <div className="mt-4 text-[10.5px] font-bold uppercase tracking-wider text-ink-tertiary">
                Application link
              </div>
              {isLoggedIn && grant.application_url ? (
                <a
                  href={grant.application_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1.5 block truncate text-[14px] font-semibold text-[color:var(--color-accent-press)] hover:underline"
                >
                  {grant.application_url}
                </a>
              ) : (
                <div
                  className="mt-1.5 truncate text-[14px] font-semibold text-ink"
                  style={{ filter: "blur(5px)" }}
                >
                  https://grantorg.io/apply/hidden-until-you-register
                </div>
              )}

              <div className="mt-4 text-[10.5px] font-bold uppercase tracking-wider text-ink-tertiary">
                Contact
              </div>
              {isLoggedIn && grant.emails && grant.emails.length > 0 ? (
                <div className="mt-1.5 flex flex-col gap-1">
                  {grant.emails.map((e) => (
                    <a
                      key={e}
                      href={`mailto:${e}`}
                      className="truncate text-[13.5px] font-medium text-[color:var(--color-accent-press)] hover:underline"
                    >
                      {e}
                    </a>
                  ))}
                </div>
              ) : (
                <div
                  className="mt-1.5 truncate text-[13.5px] font-medium text-ink"
                  style={{ filter: "blur(5px)" }}
                >
                  program-lead@grantorg.io
                </div>
              )}

              <div className="mt-5">
                {isLoggedIn && grant.application_url ? (
                  <a
                    href={grant.application_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center rounded-full bg-accent px-4 py-2.5 text-[13.5px] font-bold text-accent-ink hover:bg-accent-press"
                  >
                    Open application →
                  </a>
                ) : (
                  <Link
                    to="/login"
                    search={{ next: `/grants/${id}` }}
                    className="inline-flex w-full items-center justify-center rounded-full bg-accent px-4 py-2.5 text-[13.5px] font-bold text-accent-ink hover:bg-accent-press"
                  >
                    🔒 Free account to view application link & contact
                  </Link>
                )}
                <p className="mt-2 text-[11.5px] leading-snug text-ink-tertiary">
                  Uses your DeckScore account - one login for both.
                </p>
              </div>
            </div>

            {/* Extension upsell - inline, secondary to the grant's own apply CTA */}
            <div className="rounded-2xl border border-line bg-surface/60 p-5">
              <h3
                className="text-[15px] font-bold text-ink"
                style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
              >
                Applying to this grant?
              </h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink-secondary">
                The DeckScore extension fills grant application forms from your profile - 3 free
                forms a month, then {pricing.proMonthlyDisplay} for 25 forms a month.
              </p>
              <Link
                to="/grants/extension"
                onClick={() => {
                  try {
                    posthog.capture("extension_upsell_click", { placement: "grant_detail" });
                  } catch {
                    /* ignore */
                  }
                }}
                className="mt-4 inline-flex w-full items-center justify-center rounded-full border border-line bg-transparent px-4 py-2 text-[13px] font-semibold text-ink hover:border-accent hover:text-ink"
              >
                Get the extension →
              </Link>
            </div>
          </aside>

        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function TimeBox({ n, l }: { n: number; l: string }) {
  return (
    <div
      className="flex flex-1 flex-col items-center rounded-xl px-2 py-2"
      style={{ background: "rgba(255,255,255,0.08)" }}
    >
      <div
        className="text-[24px] font-bold leading-none text-[color:var(--color-bg)]"
        style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
      >
        {String(n).padStart(2, "0")}
      </div>
      <div
        className="mt-1 text-[9.5px] font-bold uppercase tracking-wider"
        style={{ color: "var(--color-accent-lavender)" }}
      >
        {l}
      </div>
    </div>
  );
}
